import React, { useState, useEffect } from "react";
import {
  Save,
  X,
  Pencil,
  Package,
  Clock,
  Circle,
  BarChart2,
  Hospital,
  Syringe,
  Pill,
  Plus,
  Droplets,
  Droplet,
  Bandage,
  Snowflake,
  Dna,
  Recycle,
  Timer,
} from "lucide-react";
import { Paciente } from "../../../types";
import { theme } from "../../../shared/styles/theme";
import { usePacientes } from "../../pacientes/context/PacientesContext";
import { useAuth } from "../../auth/context/AuthContext";
import {
  ehMedico,
  ehMultiprofissional,
} from "../../../shared/utils/permissoesCategoria";

interface DadosComplementaresProps {
  paciente: Paciente;
  onSalvarTx?: (dados: any) => void;
}

export default function DadosComplementares({
  paciente,
  onSalvarTx,
}: DadosComplementaresProps) {
  const { atualizarPaciente } = usePacientes();
  const { usuarioLogado } = useAuth();
  // 🔐 B88 (AI_98): dados do transplante sao campos CLINICOS do doc
  // pacientes - edicao restrita a equipe clinica (espelho das
  // firestore.rules). Nome legado: hoje significa "somente leitura"
  // (vale p/ convidado E administrativo).
  const isConvidado =
    !ehMedico(usuarioLogado) && !ehMultiprofissional(usuarioLogado);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [dados, setDados] = useState<{
    // Dados do Doador
    clampeDoador?: string;
    perfusaoPortalSolucao?: string;
    perfusaoPortalVolume?: string;
    perfusaoPortalTempo?: string;
    perfusaoArterialSolucao?: string;
    perfusaoArterialVolume?: string;
    perfusaoArterialTempo?: string;

    // Dados do Receptor
    inicioCirurgia?: string;
    fimCirurgia?: string;
    concHemacias?: string;
    plasma?: string;
    plaquetas?: string;
    crioprecipitado?: string;
    complexoProtrombina?: string;
    concFibrinogenio?: string;
    cellSaver?: string;
    atbProfilatico?: string;
    editadoEm?: string;
  }>({});

  useEffect(() => {
    const comp = paciente.dadosTransplante?.dadosComplementares;
    if (comp && Object.values(comp).some((v) => v && String(v).trim() !== "")) {
      setDados(comp);
    } else {
      setDados({});
    }
  }, [paciente.id, paciente.dadosTransplante?.dadosComplementares]);

  const temDados = Object.values(dados).some(
    (valor) => valor && valor.trim() !== ""
  );

  const handleSalvar = () => {
    const temDadosPreenchidos = Object.entries(dados).some(
      ([k, v]) => k !== "editadoEm" && v && String(v).trim() !== ""
    );
    if (!temDadosPreenchidos) {
      alert("Preencha pelo menos um campo antes de salvar.");
      return;
    }
    const dadosComTimestamp = { ...dados, editadoEm: new Date().toISOString() };
    if (onSalvarTx) {
      onSalvarTx(dadosComTimestamp);
    } else {
      atualizarPaciente(paciente.id, {
        dadosTransplante: {
          ...paciente.dadosTransplante,
          dadosComplementares: dadosComTimestamp,
        },
      });
    }
    setModoEdicao(false);
  };

  // Janela de 24h para edição
  const editadoEm = paciente.dadosTransplante?.dadosComplementares?.editadoEm;
  const horasDecorridas = editadoEm
    ? (new Date().getTime() - new Date(editadoEm).getTime()) / (1000 * 60 * 60)
    : 0;
  const podeEditar = !temDados || horasDecorridas <= 24;

  const solucoesPerfusao = [
    "Euro-Collins",
    "Belzer",
    "SPS-1",
    "IGL-1",
    "Custodiol",
    "Celsior",
    "Outro",
  ];

  return (
    <div>
      {/* BOTÕES */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "16px",
          gap: "8px",
        }}
      >
        {!isConvidado && (
          <>
            {modoEdicao ? (
              <>
                <button
                  onClick={handleSalvar}
                  style={{
                    padding: "8px 16px",
                    background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                    color: "white",
                    border: "none",
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Save size={14} style={{ marginRight: "6px" }} /> Salvar
                </button>
                <button
                  onClick={() => {
                    setModoEdicao(false);
                    if (paciente.dadosTransplante?.dadosComplementares) {
                      setDados(paciente.dadosTransplante.dadosComplementares);
                    }
                  }}
                  style={{
                    padding: "8px 16px",
                    background: theme.gray[300],
                    color: theme.gray[700],
                    border: "none",
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <X size={14} style={{ marginRight: "6px" }} /> Cancelar
                </button>
              </>
            ) : podeEditar ? (
              <button
                onClick={() => setModoEdicao(true)}
                style={{
                  padding: "8px 16px",
                  background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                  color: "white",
                  border: "none",
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Pencil size={14} style={{ marginRight: "6px" }} /> Editar
              </button>
            ) : null}
          </>
        )}
      </div>

      {/* MODO VISUALIZAÇÃO */}
      {!modoEdicao && temDados ? (
        <div>
          {/* DADOS DO DOADOR */}
          <div
            style={{
              background: `linear-gradient(135deg, #fef3c7 0%, white 100%)`,
              border: `2px solid #fbbf24`,
              borderRadius: theme.borderRadius.lg,
              padding: "20px",
              marginBottom: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#92400e",
                marginBottom: "16px",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Package size={16} style={{ marginRight: "8px" }} /> Dados do
              Doador
            </h3>

            {/* Clampe do Doador */}
            {dados.clampeDoador && (
              <div style={{ marginBottom: "16px" }}>
                <div
                  style={{
                    background: "white",
                    padding: "12px",
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid #fbbf24`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#92400e",
                      marginBottom: "4px",
                      fontFamily: theme.fonts.sans,
                      fontWeight: "600",
                    }}
                  >
                    <Clock size={14} style={{ marginRight: "6px" }} /> CLAMPE DO
                    DOADOR
                  </div>
                  <div
                    style={{
                      fontSize: "18px",
                      fontWeight: "700",
                      color: "#92400e",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {dados.clampeDoador}
                  </div>
                </div>
              </div>
            )}

            {/* Perfusão Portal */}
            {(dados.perfusaoPortalSolucao ||
              dados.perfusaoPortalVolume ||
              dados.perfusaoPortalTempo) && (
              <div style={{ marginBottom: "16px" }}>
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#92400e",
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Circle
                    size={14}
                    color="#3b82f6"
                    style={{ marginRight: "6px" }}
                  />{" "}
                  Perfusão Portal
                </h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "12px",
                  }}
                >
                  {dados.perfusaoPortalSolucao && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid #fbbf24`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#92400e",
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Droplet size={14} style={{ marginRight: "6px" }} />{" "}
                        Solução
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#92400e",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.perfusaoPortalSolucao}
                      </div>
                    </div>
                  )}
                  {dados.perfusaoPortalVolume && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid #fbbf24`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#92400e",
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <BarChart2 size={14} style={{ marginRight: "6px" }} />{" "}
                        Volume
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#92400e",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.perfusaoPortalVolume} mL
                      </div>
                    </div>
                  )}
                  {dados.perfusaoPortalTempo && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid #fbbf24`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#92400e",
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Timer size={14} style={{ marginRight: "6px" }} /> Tempo
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#92400e",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.perfusaoPortalTempo} min
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Perfusão Arterial */}
            {(dados.perfusaoArterialSolucao ||
              dados.perfusaoArterialVolume ||
              dados.perfusaoArterialTempo) && (
              <div>
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: "#92400e",
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Circle
                    size={14}
                    color="#ef4444"
                    style={{ marginRight: "6px" }}
                  />{" "}
                  Perfusão Arterial
                </h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "12px",
                  }}
                >
                  {dados.perfusaoArterialSolucao && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid #fbbf24`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#92400e",
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Droplet size={14} style={{ marginRight: "6px" }} />{" "}
                        Solução
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#92400e",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.perfusaoArterialSolucao}
                      </div>
                    </div>
                  )}
                  {dados.perfusaoArterialVolume && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid #fbbf24`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#92400e",
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <BarChart2 size={14} style={{ marginRight: "6px" }} />{" "}
                        Volume
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#92400e",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.perfusaoArterialVolume} mL
                      </div>
                    </div>
                  )}
                  {dados.perfusaoArterialTempo && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid #fbbf24`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: "#92400e",
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Timer size={14} style={{ marginRight: "6px" }} /> Tempo
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#92400e",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.perfusaoArterialTempo} min
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* DADOS DO RECEPTOR */}
          <div
            style={{
              background: `linear-gradient(135deg, ${theme.primary[50]} 0%, white 100%)`,
              border: `2px solid ${theme.primary[200]}`,
              borderRadius: theme.borderRadius.lg,
              padding: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: theme.primary[700],
                marginBottom: "16px",
                fontFamily: theme.fonts.sans,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <Hospital size={16} style={{ marginRight: "8px" }} /> Dados do
              Receptor
            </h3>

            {/* Horários da Cirurgia */}
            {(dados.inicioCirurgia || dados.fimCirurgia) && (
              <div style={{ marginBottom: "16px" }}>
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.primary[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Clock size={14} style={{ marginRight: "6px" }} /> Horários da
                  Cirurgia
                </h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)",
                    gap: "12px",
                  }}
                >
                  {dados.inicioCirurgia && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.primary[200]}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: theme.primary[600],
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Clock size={12} style={{ marginRight: "4px" }} />{" "}
                        INÍCIO
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: theme.primary[700],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.inicioCirurgia}
                      </div>
                    </div>
                  )}
                  {dados.fimCirurgia && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.primary[200]}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: theme.primary[600],
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Clock size={12} style={{ marginRight: "4px" }} /> FIM
                      </div>
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "700",
                          color: theme.primary[700],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.fimCirurgia}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Hemoterapia */}
            {(dados.concHemacias ||
              dados.plasma ||
              dados.plaquetas ||
              dados.crioprecipitado ||
              dados.complexoProtrombina ||
              dados.concFibrinogenio ||
              dados.cellSaver) && (
              <div style={{ marginBottom: "16px" }}>
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.primary[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Droplets size={14} style={{ marginRight: "6px" }} />{" "}
                  Hemoterapia
                </h4>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "12px",
                  }}
                >
                  {dados.concHemacias && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.primary[200]}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: theme.primary[600],
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Droplet size={14} style={{ marginRight: "6px" }} />{" "}
                        Hemácias
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: theme.primary[700],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.concHemacias} un
                      </div>
                    </div>
                  )}
                  {dados.plasma && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.primary[200]}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: theme.primary[600],
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Droplet size={14} style={{ marginRight: "6px" }} />{" "}
                        Plasma
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: theme.primary[700],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.plasma} un
                      </div>
                    </div>
                  )}
                  {dados.plaquetas && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.primary[200]}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: theme.primary[600],
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Bandage size={14} style={{ marginRight: "6px" }} />{" "}
                        Plaquetas
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: theme.primary[700],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.plaquetas} un
                      </div>
                    </div>
                  )}
                  {dados.crioprecipitado && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.primary[200]}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: theme.primary[600],
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Snowflake size={14} style={{ marginRight: "6px" }} />{" "}
                        Crioprecipitado
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: theme.primary[700],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.crioprecipitado} un
                      </div>
                    </div>
                  )}
                  {dados.complexoProtrombina && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.primary[200]}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: theme.primary[600],
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Syringe size={14} style={{ marginRight: "6px" }} />{" "}
                        Compl. Protromb.
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: theme.primary[700],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.complexoProtrombina} un
                      </div>
                    </div>
                  )}
                  {dados.concFibrinogenio && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.primary[200]}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: theme.primary[600],
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Dna size={14} style={{ marginRight: "6px" }} />{" "}
                        Fibrinogênio
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: theme.primary[700],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.concFibrinogenio} un
                      </div>
                    </div>
                  )}
                  {dados.cellSaver && (
                    <div
                      style={{
                        background: "white",
                        padding: "12px",
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.primary[200]}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: "11px",
                          color: theme.primary[600],
                          marginBottom: "4px",
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        <Recycle size={14} style={{ marginRight: "6px" }} />{" "}
                        Cell Saver
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          color: theme.primary[700],
                          fontFamily: theme.fonts.sans,
                        }}
                      >
                        {dados.cellSaver} mL
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Antibiótico */}
            {dados.atbProfilatico && (
              <div>
                <h4
                  style={{
                    fontSize: "14px",
                    fontWeight: "600",
                    color: theme.primary[700],
                    marginBottom: "8px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Pill size={14} style={{ marginRight: "6px" }} /> Antibiótico
                  Profilático
                </h4>
                <div
                  style={{
                    background: "white",
                    padding: "16px",
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.primary[200]}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: "15px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    {dados.atbProfilatico}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : !modoEdicao && !temDados ? (
        // ESTADO VAZIO
        <div
          style={{
            textAlign: "center",
            padding: "48px 20px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.lg,
            border: `2px dashed ${theme.gray[300]}`,
          }}
        >
          <BarChart2
            size={48}
            color={theme.gray[300]}
            style={{ marginBottom: "16px" }}
          />
          <p
            style={{
              color: theme.gray[600],
              fontSize: "14px",
              fontFamily: theme.fonts.sans,
              marginBottom: "16px",
            }}
          >
            Nenhum dado complementar cadastrado ainda
          </p>
          {!isConvidado && (
            <button
              onClick={() => setModoEdicao(true)}
              style={{
                padding: "10px 20px",
                background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                color: "white",
                border: "none",
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
              }}
            >
              <Plus size={14} style={{ marginRight: "6px" }} /> Cadastrar Dados
            </button>
          )}
        </div>
      ) : (
        // MODO EDIÇÃO
        <div>
          {/* DADOS DO DOADOR - MODO EDIÇÃO */}
          <div
            style={{
              background: "#fef3c7",
              border: `2px solid #fbbf24`,
              borderRadius: theme.borderRadius.lg,
              padding: "20px",
              marginBottom: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: "#92400e",
                marginBottom: "16px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <Package size={16} style={{ marginRight: "8px" }} /> Dados do
              Doador
            </h3>

            {/* Clampe do Doador */}
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#92400e",
                  marginBottom: "8px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Clock size={14} style={{ marginRight: "6px" }} /> Clampe do
                Doador
              </label>
              <input
                type="time"
                value={dados.clampeDoador || ""}
                onChange={(e) =>
                  setDados({ ...dados, clampeDoador: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `2px solid #fbbf24`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontFamily: theme.fonts.sans,
                  boxSizing: "border-box",
                }}
              />
            </div>

            {/* Perfusão Portal */}
            <div style={{ marginBottom: "16px" }}>
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#92400e",
                  marginBottom: "12px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Circle
                  size={14}
                  color="#3b82f6"
                  style={{ marginRight: "6px" }}
                />{" "}
                Perfusão Portal
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr",
                  gap: "12px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#92400e",
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Droplet size={14} style={{ marginRight: "6px" }} /> Solução
                  </label>
                  <select
                    value={dados.perfusaoPortalSolucao || ""}
                    onChange={(e) =>
                      setDados({
                        ...dados,
                        perfusaoPortalSolucao: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid #fbbf24`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="">Selecione...</option>
                    {solucoesPerfusao.map((sol) => (
                      <option key={sol} value={sol}>
                        {sol}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#92400e",
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <BarChart2 size={14} style={{ marginRight: "6px" }} />{" "}
                    Volume (mL)
                  </label>
                  <input
                    type="number"
                    value={dados.perfusaoPortalVolume || ""}
                    onChange={(e) =>
                      setDados({
                        ...dados,
                        perfusaoPortalVolume: e.target.value,
                      })
                    }
                    placeholder="500"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid #fbbf24`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#92400e",
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Timer size={14} style={{ marginRight: "6px" }} /> Tempo
                    (min)
                  </label>
                  <input
                    type="number"
                    value={dados.perfusaoPortalTempo || ""}
                    onChange={(e) =>
                      setDados({
                        ...dados,
                        perfusaoPortalTempo: e.target.value,
                      })
                    }
                    placeholder="15"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid #fbbf24`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Perfusão Arterial */}
            <div>
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: "#92400e",
                  marginBottom: "12px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Circle
                  size={14}
                  color="#ef4444"
                  style={{ marginRight: "6px" }}
                />{" "}
                Perfusão Arterial
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1fr 1fr",
                  gap: "12px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#92400e",
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Droplet size={14} style={{ marginRight: "6px" }} /> Solução
                  </label>
                  <select
                    value={dados.perfusaoArterialSolucao || ""}
                    onChange={(e) =>
                      setDados({
                        ...dados,
                        perfusaoArterialSolucao: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid #fbbf24`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="">Selecione...</option>
                    {solucoesPerfusao.map((sol) => (
                      <option key={sol} value={sol}>
                        {sol}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#92400e",
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <BarChart2 size={14} style={{ marginRight: "6px" }} />{" "}
                    Volume (mL)
                  </label>
                  <input
                    type="number"
                    value={dados.perfusaoArterialVolume || ""}
                    onChange={(e) =>
                      setDados({
                        ...dados,
                        perfusaoArterialVolume: e.target.value,
                      })
                    }
                    placeholder="500"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid #fbbf24`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: "#92400e",
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Timer size={14} style={{ marginRight: "6px" }} /> Tempo
                    (min)
                  </label>
                  <input
                    type="number"
                    value={dados.perfusaoArterialTempo || ""}
                    onChange={(e) =>
                      setDados({
                        ...dados,
                        perfusaoArterialTempo: e.target.value,
                      })
                    }
                    placeholder="15"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid #fbbf24`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* DADOS DO RECEPTOR - MODO EDIÇÃO */}
          <div
            style={{
              background: theme.primary[50],
              border: `2px solid ${theme.primary[200]}`,
              borderRadius: theme.borderRadius.lg,
              padding: "20px",
            }}
          >
            <h3
              style={{
                fontSize: "18px",
                fontWeight: "700",
                color: theme.primary[700],
                marginBottom: "16px",
                fontFamily: theme.fonts.sans,
              }}
            >
              <Hospital size={16} style={{ marginRight: "8px" }} /> Dados do
              Receptor
            </h3>

            {/* Horários */}
            <div style={{ marginBottom: "16px" }}>
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme.primary[700],
                  marginBottom: "12px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Clock size={14} style={{ marginRight: "6px" }} /> Horários da
                Cirurgia
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "12px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Clock size={12} style={{ marginRight: "4px" }} /> Início da
                    Cirurgia
                  </label>
                  <input
                    type="time"
                    value={dados.inicioCirurgia || ""}
                    onChange={(e) =>
                      setDados({ ...dados, inicioCirurgia: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid ${theme.primary[300]}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Clock size={12} style={{ marginRight: "4px" }} /> Fim da
                    Cirurgia
                  </label>
                  <input
                    type="time"
                    value={dados.fimCirurgia || ""}
                    onChange={(e) =>
                      setDados({ ...dados, fimCirurgia: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid ${theme.primary[300]}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Hemoterapia */}
            <div style={{ marginBottom: "16px" }}>
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme.primary[700],
                  marginBottom: "12px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Droplets size={14} style={{ marginRight: "6px" }} />{" "}
                Hemoterapia
              </h4>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "12px",
                }}
              >
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Droplet size={14} style={{ marginRight: "6px" }} />{" "}
                    Hemácias (un)
                  </label>
                  <input
                    type="number"
                    value={dados.concHemacias || ""}
                    onChange={(e) =>
                      setDados({ ...dados, concHemacias: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid ${theme.primary[300]}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Droplet size={14} style={{ marginRight: "6px" }} /> Plasma
                    (un)
                  </label>
                  <input
                    type="number"
                    value={dados.plasma || ""}
                    onChange={(e) =>
                      setDados({ ...dados, plasma: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid ${theme.primary[300]}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Bandage size={14} style={{ marginRight: "6px" }} />{" "}
                    Plaquetas (un)
                  </label>
                  <input
                    type="number"
                    value={dados.plaquetas || ""}
                    onChange={(e) =>
                      setDados({ ...dados, plaquetas: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid ${theme.primary[300]}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Snowflake size={14} style={{ marginRight: "6px" }} />{" "}
                    Crioprecipitado (un)
                  </label>
                  <input
                    type="number"
                    value={dados.crioprecipitado || ""}
                    onChange={(e) =>
                      setDados({ ...dados, crioprecipitado: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid ${theme.primary[300]}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Syringe size={14} style={{ marginRight: "6px" }} /> Compl.
                    Protromb. (un)
                  </label>
                  <input
                    type="number"
                    value={dados.complexoProtrombina || ""}
                    onChange={(e) =>
                      setDados({
                        ...dados,
                        complexoProtrombina: e.target.value,
                      })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid ${theme.primary[300]}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Dna size={14} style={{ marginRight: "6px" }} />{" "}
                    Fibrinogênio (un)
                  </label>
                  <input
                    type="number"
                    value={dados.concFibrinogenio || ""}
                    onChange={(e) =>
                      setDados({ ...dados, concFibrinogenio: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid ${theme.primary[300]}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "600",
                      color: theme.primary[700],
                      marginBottom: "6px",
                      fontFamily: theme.fonts.sans,
                    }}
                  >
                    <Recycle size={14} style={{ marginRight: "6px" }} /> Cell
                    Saver (mL)
                  </label>
                  <input
                    type="number"
                    value={dados.cellSaver || ""}
                    onChange={(e) =>
                      setDados({ ...dados, cellSaver: e.target.value })
                    }
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: `2px solid ${theme.primary[300]}`,
                      borderRadius: theme.borderRadius.md,
                      fontSize: "14px",
                      fontFamily: theme.fonts.sans,
                      boxSizing: "border-box",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Antibiótico */}
            <div>
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme.primary[700],
                  marginBottom: "12px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Pill size={14} style={{ marginRight: "6px" }} /> Antibiótico
                Profilático
              </h4>
              <input
                type="text"
                value={dados.atbProfilatico || ""}
                onChange={(e) =>
                  setDados({ ...dados, atbProfilatico: e.target.value })
                }
                placeholder="Ex: Cefazolina 2g EV"
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `2px solid ${theme.primary[300]}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontFamily: theme.fonts.sans,
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
